import { Env } from "@/libs/Env";

import { createServerInstance, makeClientConfig } from "@repo/rollbar";

// FOUNDER-QA-FOLLOWUP-20260918-008/009 — root's Rollbar wiring carried two silent defects, both
// inherited unnoticed from the accounts/vendors pattern this was copied from:
//
// 1. NO `codeVersion` WAS EVER PASSED. `@repo/rollbar`'s `config.codeVersion ?? "1.0.0"` then
//    defaulted every event in every environment to the literal string "1.0.0" forever — Rollbar's
//    release view could never tell one deploy from another. `SG_BUILD_COMMIT` is the full candidate
//    SHA already stamped at build time (next.config.ts `buildStamp()`, served back by `/api/build`)
//    — the same identity this packet's evidence already keys everything else off, reused here rather
//    than invented. `env: buildStamp()` in next.config.ts exposes it to BOTH the server and the
//    browser bundle (Next's `env` key, unlike `NEXT_PUBLIC_*`, is not client-only by name — it is
//    statically inlined into both), so one constant covers both instances below.
// 2. `environment: Env.NODE_ENV` IS NOT AN ENVIRONMENT. A production build's `NODE_ENV` is always
//    the literal string "production" whether it is running on staging or on www.snowbus.com, so
//    every staging error and every real production error landed in Rollbar under the same label.
//    `STAGING_NOINDEX` is this repo's own existing staging signal (next.config.ts already gates the
//    noindex header on it) — reused here rather than adding a second variable for the same fact.
// Not part of Env.ts's Zod schema on purpose, matching the existing pattern in
// apps/root/src/app/api/legacy/booking/submit/route.ts for the same reason: these are build-time
// STAMPED values (next.config.ts buildStamp()/STAGING_NOINDEX), not runtime-configured secrets or
// URLs a developer sets per environment, so validating them through Env would be validating a
// constant the build process itself already guarantees the shape of.
// eslint-disable-next-line no-restricted-properties
const CODE_VERSION = process.env.SG_BUILD_COMMIT ?? "unknown";
// eslint-disable-next-line no-restricted-properties
const ENVIRONMENT = process.env.STAGING_NOINDEX === "1" ? "staging" : (Env.NODE_ENV ?? "development");

// SCRUBBING — no secret has ever been observed in a root event, but this app posts booking payloads
// (name/email/phone, §WO-COMMERCE-BIND-001) and carries auth cookies/headers on API routes, and
// Rollbar's own default scrub list does not know this app's field names. Added, not assumed: Rollbar
// scrubs `scrubFields` wherever they appear in the payload (body, query, headers), case-insensitively.
const SCRUB_FIELDS = [
  "authorization",
  "cookie",
  "set-cookie",
  "password",
  "token",
  "access_token",
  "refresh_token",
  "email",
  "phone",
  "cardNumber",
  "cvv",
];

export const clientConfig = makeClientConfig({
  accessToken: Env.NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN,
  environment: ENVIRONMENT,
  codeVersion: CODE_VERSION,
});

/** Merged in below via `.configure()` / spread, since `@repo/rollbar`'s `RollbarConfig`
 *  (packages/rollbar/src/index.ts) does not expose `scrubFields` as a typed field and that package is
 *  out of this packet's allowed_paths (apps/root/, tooling/port-harness/, docs/evidence/) to extend.
 *  Exported so instrumentation-client.ts can apply the same list to the browser instance. */
export const scrubConfig = { scrubFields: SCRUB_FIELDS, scrubHeaders: ["authorization", "cookie"] };

export const serverInstance =
  typeof window === "undefined"
    ? createServerInstance({
        accessToken: Env.ROLLBAR_SERVER_TOKEN,
        environment: ENVIRONMENT,
        codeVersion: CODE_VERSION,
      })
    : null;
serverInstance?.configure(scrubConfig);
